import React from 'react';
import classnames from 'classnames';
import { Checkbox, CheckboxGroupProps, CheckboxProps, CheckChangeContext } from 'tea-component';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { LANGUAGE_CN } from '@/pages/intelligence/_config/config';

import './index.scss';

const formatOptions = (options: IIconableCheckboxSelectProps['options']): ICheckboxOption[] => {
  if (!Array.isArray(options)) return [];
  return options.map((option) => {
    if (typeof option === 'object') return option;
    return {
      text: option,
      value: option,
      icon: option,
    };
  });
};

interface ICheckboxOption extends Omit<CheckboxProps, 'value'> {
  text: string;
  textEN?: string;
  textCN?: string;
  value: string;
  // icon可以不配置，没配置icon的话，会默认用value作为icon的key
  icon?: string;
}

interface IIconableCheckboxSelectProps extends CheckboxGroupProps {
  locale: string;
  // 是否必选
  required?: boolean;
  // onChange回传的value 是否需要按照options的顺序排序
  sortable?: boolean;
  options: string[] | ICheckboxOption[];
  // 根据icon 获取 icon图片的方法，默认为getDataSourceIcon方法（根据数据源key获取数据源icon）
  getIconSrc?: (icon: string) => string;
}

const IconableCheckboxSelect: React.FC<IIconableCheckboxSelectProps> = (props) => {
  const {
    style,
    className,
    locale,
    required = true,
    disabled = false,
    sortable = true,
    options: optionsOuter,
    value,
    onChange,
    getIconSrc = getDataSourceIcon,
    ...restProps
  } = props;

  const isCN = locale === LANGUAGE_CN;
  const textKey = isCN ? 'textCN' : 'textEN';

  const options = formatOptions(optionsOuter);

  const getValuePayload = (payload: string[]): string[] => {
    if (!Array.isArray(payload)) return [];
    if (!sortable) return payload;
    return [...payload]?.sort((a, b) => {
      const aIdx = options?.findIndex((option) => option?.value === a);
      const bIdx = options?.findIndex((option) => option?.value === b);
      return aIdx - bIdx;
    });
  };

  const handleChange = (payload: string[], context: CheckChangeContext) => {
    // 必选时，至少勾选一个选项
    if (required && !payload?.length) return;
    const payloadTmp = getValuePayload(payload);
    onChange?.(payloadTmp, context);
  };

  return (
    <Checkbox.Group
      {...restProps}
      style={style}
      className={classnames('dbi-iconable-checkbox-select search-item', className)}
      disabled={disabled}
      value={value}
      onChange={handleChange}
    >
      {options?.map((option) => {
        const { value: optionValue, ...restOptionProps } = option || {};
        const optionText = option?.[textKey] || option?.text;
        const optionIcon = option?.icon || option?.value;
        return (
          <Checkbox {...restOptionProps} key={optionValue} name={optionValue}>
            {optionIcon && (
              <img className="dbi-iconable-checkbox-select_icon" src={getIconSrc(optionIcon)} alt="" />
            )}
            <span className="dbi-iconable-checkbox-select_text">{optionText}</span>
          </Checkbox>
        );
      })}
    </Checkbox.Group>
  );
};

export default IconableCheckboxSelect;
